import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Heart, MessageCircle, Send, Loader2, Share2, Link2, Calendar, MapPin } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Spinner, SectionLabel, Pill } from "../components/Primitives";
import { photoSrc } from "./Stories";

export default function StoryDetail() {
  const { storyId } = useParams();
  const { user, login } = useAuth();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [likes, setLikes] = useState(0);
  const [liked, setLiked] = useState(false);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    api.get(`/stories/${storyId}`).then((r) => {
      setStory(r.data);
      setLikes(r.data.like_count || 0);
    }).catch(() => setStory(null)).finally(() => setLoading(false));
    api.get(`/stories/${storyId}/reactions`).then((r) => {
      setLikes(r.data.likes); setLiked(r.data.liked); setNotes(r.data.notes);
    }).catch(() => {});
  }, [storyId]);

  const toggleLike = async () => {
    if (!user) return login();
    try {
      const r = await api.post(`/stories/${storyId}/like`);
      setLikes(r.data.likes); setLiked(r.data.liked);
    } catch { toast.error("Could not react"); }
  };

  const postNote = async (e) => {
    e.preventDefault();
    if (!user) return login();
    if (!noteText.trim()) return;
    setPosting(true);
    try {
      const r = await api.post(`/stories/${storyId}/notes`, { text: noteText.trim() });
      setNotes((n) => [r.data, ...n]);
      setNoteText("");
      toast.success("Note added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add note");
    } finally {
      setPosting(false);
    }
  };

  const pageUrl = `${window.location.origin}/stories/${storyId}`;
  const shareText = story ? `"${story.title}" — a heritage story on Our African Narratives` : "";

  const copyLink = async () => {
    await navigator.clipboard.writeText(pageUrl);
    toast.success("Link copied — share it anywhere");
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: story.title, text: shareText, url: pageUrl }); } catch {}
    } else {
      copyLink();
    }
  };

  if (loading) return <div className="min-h-[60vh]"><Spinner /></div>;
  if (!story) return (
    <div className="mx-auto max-w-xl px-6 py-32 text-center">
      <p className="text-muted" data-testid="story-not-found">This story isn't available.</p>
      <Link to="/stories" className="mt-4 inline-block text-gold hover:underline">← All heritage stories</Link>
    </div>
  );

  return (
    <div data-testid="story-page" className="mx-auto max-w-3xl px-6 py-12 md:px-12">
      <Link to="/stories" className="mb-6 flex items-center gap-2 text-sm text-muted hover:text-gold">
        <ArrowLeft className="h-4 w-4" /> All heritage stories
      </Link>

      {story.photo_url && (
        <div className="mb-8 max-h-96 overflow-hidden border border-edge">
          <img src={photoSrc(story.photo_url)} alt="" className="h-full w-full object-cover" />
        </div>
      )}

      <SectionLabel>Heritage Story</SectionLabel>
      <h1 className="font-serif text-4xl font-semibold leading-tight text-bone lg:text-5xl">{story.title}</h1>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Pill tone="gold"><Calendar className="mr-1 inline h-3 w-3" />{story.year_label}</Pill>
        <Pill tone="muted"><MapPin className="mr-1 inline h-3 w-3" />{story.region}</Pill>
      </div>
      <p className="mt-2 text-sm text-muted">Shared by {story.author_name}</p>

      <p className="mt-8 whitespace-pre-line text-lg leading-relaxed text-[#cfc9bd]">{story.body}</p>

      {/* SHARE */}
      <div className="mt-10 flex flex-wrap items-center gap-2 border-t border-edge pt-6">
        <button onClick={toggleLike} data-testid="story-like-btn"
          className={`flex items-center gap-2 border px-4 py-2 text-sm transition-colors ${liked ? "border-blood bg-blood/10 text-[#e07a72]" : "border-edge text-bone hover:border-blood/60"}`}>
          <Heart className={`h-4 w-4 ${liked ? "fill-current" : ""}`} />
          <span data-testid="story-like-count">{likes}</span> {likes === 1 ? "heart" : "hearts"}
        </button>
        <button onClick={nativeShare} data-testid="story-share-btn"
          className="flex items-center gap-2 border border-edge px-4 py-2 text-sm text-bone transition-colors hover:border-gold hover:text-gold">
          <Share2 className="h-4 w-4" /> Share
        </button>
        <button onClick={copyLink} data-testid="story-copy-link-btn"
          className="flex items-center gap-2 border border-edge px-4 py-2 text-sm text-bone transition-colors hover:border-gold hover:text-gold">
          <Link2 className="h-4 w-4" /> Copy link
        </button>
        <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(pageUrl)}`}
          target="_blank" rel="noreferrer" data-testid="story-share-x"
          className="border border-edge px-4 py-2 text-sm text-bone transition-colors hover:border-gold hover:text-gold">X</a>
        <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`}
          target="_blank" rel="noreferrer" data-testid="story-share-fb"
          className="border border-edge px-4 py-2 text-sm text-bone transition-colors hover:border-gold hover:text-gold">Facebook</a>
      </div>

      {/* NOTES */}
      <div className="mt-8">
        <h3 className="mb-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-gold">
          <MessageCircle className="h-4 w-4" /> Notes ({notes.length})
        </h3>
        <form onSubmit={postNote} className="mb-4 flex gap-2" data-testid="story-note-form">
          <input value={noteText} onChange={(e) => setNoteText(e.target.value)} maxLength={500}
            placeholder={user ? "Leave a short note…" : "Sign in to leave a note"}
            data-testid="story-note-input"
            className="flex-1 border border-edge bg-ash px-4 py-2.5 text-sm text-bone placeholder:text-muted focus:border-gold focus:outline-none" />
          <button type="submit" disabled={posting} data-testid="story-note-submit"
            className="flex items-center gap-1 border border-gold bg-gold px-4 text-sm font-semibold text-ink disabled:opacity-50">
            {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </form>
        <div className="space-y-3">
          {notes.map((n) => (
            <div key={n.id} className="border-l-2 border-edge pl-4" data-testid={`story-note-${n.id}`}>
              <p className="text-sm text-[#cfc9bd]">{n.text}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.12em] text-muted">— {n.author_name}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
